import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import AdminTable from "../components/AdminTable";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import "./Admin.css";

function AdminDashboard() {
  const [grievances, setGrievances] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchGrievances();
  }, []);

  const fetchGrievances = async () => {
    const res = await axios.get("https://grs.jeccac.in/api/admin/grievances", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
      },
    });
    setGrievances(res.data);
  };

  const getExportData = () => {
    return grievances.map((g, index) => [
      index + 1,
      g.name,
      g.category,
      g.admissionNo || "-",
      g.email,
      g.mobile,
      g.grievanceType,
      g.description,
      g.status,
    ]);
  };

  const exportHeaders = [
    [
      "SL NO",
      "NAME",
      "CATEGORY",
      "ADMN NO",
      "EMAIL",
      "PHONE NO",
      "GRIEVANCE TYPE",
      "DETAILS",
      "STATUS",
    ],
  ];

  const handleCopy = () => {
    const data = getExportData();
    const textData = [
      exportHeaders[0].join("\t"),
      ...data.map((row) => row.join("\t")),
    ].join("\n");
    navigator.clipboard
      .writeText(textData)
      .then(() => alert("Table data copied to clipboard!"))
      .catch(() => alert("Failed to copy to clipboard."));
  };

  const handlePDF = () => {
    const doc = new jsPDF("landscape");
    doc.text("Grievance Details", 14, 15);
    autoTable(doc, {
      head: exportHeaders,
      body: getExportData(),
      startY: 20,
      styles: { fontSize: 8 },
    });
    doc.save("Grievances.pdf");
  };

  const handleCSV = () => {
    const worksheet = XLSX.utils.aoa_to_sheet([
      ...exportHeaders,
      ...getExportData(),
    ]);
    const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csvOutput], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Grievances.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExcel = () => {
    const worksheet = XLSX.utils.aoa_to_sheet([
      ...exportHeaders,
      ...getExportData(),
    ]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Grievances");
    XLSX.writeFile(workbook, "Grievances.xlsx");
  };

  const handleCleanTestUsers = async () => {
    if (
      !window.confirm(
        "Are you sure you want to remove all test user grievances from the database?"
      )
    )
      return;
    try {
      const res = await axios.delete(
        "https://grs.jeccac.in/api/admin/grievances/test-users",
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        }
      );
      alert(`Removed ${res.data.deletedCount || 0} test user record(s).`);
      fetchGrievances();
    } catch (err) {
      alert("Failed to remove test users.");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin");
  };

  return (
    <div className="admin-container">
      {/* Sidebar */}
      <div className={`sidebar ${sidebarCollapsed ? "collapsed" : ""}`}>
        <div className="sidebar-header">
          <span
            style={{
              cursor: "pointer",
              fontSize: "1.4em",
              marginRight: sidebarCollapsed ? "0" : "15px",
            }}
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            ☰
          </span>
          {!sidebarCollapsed && (
            <span style={{ fontSize: "1.1em" }}>Grievance Admin</span>
          )}
        </div>
        <div className="sidebar-profile">
          <img
            src="https://ui-avatars.com/api/?name=Admin&background=4285f4&color=fff&rounded=true&font-size=0.4"
            alt="Admin"
            style={{
              width: sidebarCollapsed ? "40px" : "60px",
              height: sidebarCollapsed ? "40px" : "60px",
              transition: "0.3s",
            }}
          />
          {!sidebarCollapsed && (
            <div
              style={{ fontSize: "0.9rem", color: "#666", marginTop: "10px" }}
            >
              Admin <span style={{ fontSize: "0.7rem" }}>▼</span>
            </div>
          )}
        </div>

        <ul className="sidebar-menu">
          {!sidebarCollapsed && <div className="menu-label">Dashboard</div>}
          <li className="menu-item" title="Dashboard">
            <span className="sidebar-icon">⌂</span>
            {!sidebarCollapsed && "Dashboard"}
          </li>

          {!sidebarCollapsed && <div className="menu-label">Pages</div>}
          <li className="menu-item active" title="Grievance">
            <span className="sidebar-icon">📦</span>
            {!sidebarCollapsed && (
              <span style={{ display: "flex", flex: 1 }}>
                Grievance{" "}
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: "0.7rem",
                    marginTop: "4px",
                  }}
                >
                  ▼
                </span>
              </span>
            )}
          </li>

          {!sidebarCollapsed && (
            <ul className="sidebar-menu menu-sub">
              <li className="menu-item">
                <span
                  style={{
                    color: "#4285f4",
                    marginRight: "8px",
                    fontWeight: "bold",
                  }}
                >
                  ›
                </span>{" "}
                Grievance
              </li>
            </ul>
          )}

          {!sidebarCollapsed && <div className="menu-label">Account</div>}
          <li
            className="menu-item"
            title="Logout"
            onClick={handleLogout}
            style={{ color: "#d9534f" }}
          >
            <span className="sidebar-icon">🚪</span>
            {!sidebarCollapsed && "Logout"}
          </li>
        </ul>
      </div>

      {/* Main Content */}
      <div className="main-content">
        <div className="topbar">
          <h2>Dashboard Analytics</h2>
          <div className="breadcrumb">⌂ / Dashboard Analytics</div>
        </div>

        <div className="content-wrapper">
          <div className="card">
            <div className="card-header">Grievance Details</div>

            <div className="table-toolbar">
              <div>
                <button className="export-btn" onClick={handleCopy}>
                  Copy
                </button>
                <button className="export-btn" onClick={handlePDF}>
                  PDF
                </button>
                <button className="export-btn" onClick={handleCSV}>
                  CSV
                </button>
                <button className="export-btn" onClick={handleExcel}>
                  Excel
                </button>
                <button
                  className="clean-test-btn"
                  onClick={handleCleanTestUsers}
                >
                  🗑️ Remove Test Users
                </button>
              </div>
              <div>
                <label
                  style={{
                    fontSize: "0.9rem",
                    color: "#555",
                    marginRight: "8px",
                  }}
                >
                  Search:{" "}
                </label>
                <input type="text" className="search-box" />
              </div>
            </div>

            <AdminTable
              grievances={grievances}
              onReplySubmitted={fetchGrievances}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
